// URL-synced sort state for sortable tables. Deliberately separate from
// useV2Filters: sort isn't a filter — folding it into that hook's PARAM map
// would make clearAll() wipe it and hasActiveFilters true, changing behavior
// for every page that already consumes those. Modeled on useV2Filters' own
// validated-enum handling of `time` (useV2Filters.ts).

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export type SortDir = 'asc' | 'desc';

export interface TableSort<K extends string> {
  sortKey: K;
  sortDir: SortDir;
  /** Toggle direction if `key` is already active, otherwise switch to `key`. */
  toggleSort: (key: K) => void;
}

/**
 * `validKeys`/`defaultKey` scope which `?sort=` values are honored — an
 * invalid or missing value (e.g. a stale link from a previous column set)
 * falls back to `defaultKey`/`defaultDir` rather than producing garbage sort
 * order. `nextDirFor` picks the starting direction when switching onto a
 * column for the first time (defaults to always `asc`).
 */
export function useTableSort<K extends string>(
  validKeys: readonly K[],
  defaultKey: K,
  defaultDir: SortDir,
  nextDirFor?: (key: K) => SortDir
): TableSort<K> {
  const [searchParams, setSearchParams] = useSearchParams();

  const sortKey: K = useMemo(() => {
    const val = searchParams.get('sort');
    return val && (validKeys as readonly string[]).includes(val) ? (val as K) : defaultKey;
  }, [searchParams, validKeys, defaultKey]);

  const sortDir: SortDir = useMemo(() => {
    const val = searchParams.get('dir');
    return val === 'asc' || val === 'desc' ? val : defaultDir;
  }, [searchParams, defaultDir]);

  const toggleSort = useCallback(
    (key: K) => {
      const isActive = key === sortKey;
      const nextDir: SortDir = isActive
        ? sortDir === 'asc'
          ? 'desc'
          : 'asc'
        : nextDirFor
        ? nextDirFor(key)
        : 'asc';

      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          if (key === defaultKey) next.delete('sort');
          else next.set('sort', key);
          if (nextDir === defaultDir) next.delete('dir');
          else next.set('dir', nextDir);
          return next;
        },
        { replace: true }
      );
    },
    [sortKey, sortDir, nextDirFor, defaultKey, defaultDir, setSearchParams]
  );

  return { sortKey, sortDir, toggleSort };
}
